<template>
  <div class="login-page">
    <transition name="splash-fade">
      <div
        v-if="showSplash"
        class="login-splash"
        aria-hidden="true"
      >
        <div class="login-splash__inner">
          <h1 class="login-splash__brand">
            <span
              v-for="(char, index) in splashBrandChars"
              :key="index"
              class="login-splash__char"
              :class="{ 'login-splash__char--hyphen': char === '-' }"
              :style="{ animationDelay: `${index * 0.055}s` }"
            >{{ char }}</span>
          </h1>
          <p class="login-splash__tagline">让收藏，用得上</p>
        </div>
      </div>
    </transition>
    <el-form
      ref="loginForm"
      :model="form"
      :rules="rules"
      class="login-form-wrap"
      @submit.native.prevent
    >
      <div class="login-content">
        <h1 class="login-title">
          登录
        </h1>
        <p class="login-subtitle">欢迎回来，请登录您的账号</p>
        <div class="login-form">
          <el-form-item prop="username">
            <el-input
              v-model="form.username"
              placeholder="账号"
              @input="onInput"
              size="large"
              clearable
              class="login-input"
            >
              <i slot="prefix" class="el-input__icon el-icon-user"></i>
            </el-input>
          </el-form-item>
          <el-form-item prop="password">
            <el-input
              v-model="form.password"
              type="password"
              placeholder="密码"
              size="large"
              @input="onInput"
              show-password
              clearable
              class="login-input"
              @keyup.enter.native="handleLogin"
            >
              <i slot="prefix" class="el-input__icon el-icon-lock"></i>
            </el-input>
          </el-form-item>
          <el-form-item class="login-remember-item">
            <el-checkbox v-model="form.rememberMe">记住我</el-checkbox>
          </el-form-item>
          <el-form-item>
            <el-button
              type="primary"
              class="login-btn"
              :loading="loading"
              @click="handleLogin"
            >
              <i v-if="!loading" class="el-icon-right login-btn__icon"></i>
              登录
            </el-button>
          </el-form-item>
          <p class="register-to-login">
            <span class="register-to-login__left">
              还没有账号？
              <router-link class="login-agree-link" to="/register">去注册</router-link>
            </span>
            <router-link
              class="login-agree-link register-to-login__right"
              to="/change-password"
            >忘记密码？</router-link>
          </p>
        </div>
      </div>
      <div class="login-agree-outer">
        <el-form-item prop="agreed" class="login-agree-item">
          <el-checkbox v-model="form.agreed">
            <span class="login-agree-text">我已阅读并同意</span>
            <router-link
              class="login-agree-link"
              to="/user-agreement"
              @click.native.stop
            >《用户协议》</router-link>
            <span class="login-agree-text">与</span>
            <router-link
              class="login-agree-link"
              to="/privacy-policy"
              @click.native.stop
            >《隐私政策》</router-link>
          </el-checkbox>
        </el-form-item>
      </div>
    </el-form>
  </div>
</template>

<script>
import axios from 'axios'
import { getApiErrorMessage } from '@/utils/apiErrorMessage'
import { clearUserProfile, getSavedUsername, isRememberMeEnabled, saveSavedUsername } from '@/utils/userSession'
import { applyLoginOrRefreshPayload } from '@/utils/authPayload'

export default {
  name: 'LoginPage',
  data() {
    return {
      showSplash: true,
      splashTimer: null,
      form: {
        username: '',
        password: '',
        rememberMe: true,
        agreed: false
      },
      rules: {
        username: [
          { required: true, message: '请输入账号', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' }
        ],
        agreed: [
          {
            validator: (_, value, callback) => {
              if (!value) {
                callback(new Error('请阅读并同意用户协议和隐私政策'))
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ]
      },
      loading: false,
      loginError: false
    }
  },
  computed: {
    splashBrandChars() {
      return 'InsightPod'.split('')
    }
  },
  mounted() {
    this.form.username = getSavedUsername()
    this.form.rememberMe = isRememberMeEnabled() || this.form.rememberMe
    this.splashTimer = window.setTimeout(() => {
      this.showSplash = false
      this.splashTimer = null
    }, 1700)
  },
  beforeDestroy() {
    if (this.splashTimer != null) {
      window.clearTimeout(this.splashTimer)
    }
  },
  methods: {
    onInput() {
      this.loginError = false
    },
    handleLogin() {
      this.$refs.loginForm.validate(async (valid) => {
        if (!valid) return

        this.loading = true
        try {
          const response = await axios.post(
            '/api/app/core/user/login',
            {
              username: this.form.username.trim(),
              password: this.form.password,
              rememberMe: this.form.rememberMe
            },
            { skipAuth: true }
          )
          clearUserProfile()
          if (this.form.rememberMe) {
            saveSavedUsername(this.form.username.trim())
          } else {
            saveSavedUsername('')
          }
          applyLoginOrRefreshPayload(response.data, {
            fallbackUsername: this.form.username.trim(),
            rememberMe: this.form.rememberMe
          })
          this.$message.success('登录成功')
          // 登录成功后默认进入内容列表页
          this.$router.replace('/summary-list')
        } catch (error) {
          console.error('登录失败:', error)
          this.loginError = true
          this.$message.error(getApiErrorMessage(error, '登录失败，请重试'))
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
/* 启动页：白底蓝字、逐字显现 + 扫光 */
.login-splash {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f2f6ff;
  padding: env(safe-area-inset-top, 0px) max(28px, env(safe-area-inset-right, 0px))
    env(safe-area-inset-bottom, 0px) max(28px, env(safe-area-inset-left, 0px));
  box-sizing: border-box;
}

.login-splash__inner {
  position: relative;
  width: min(520px, 100%);
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  text-align: center;
  transform: translateY(-110px);
}

.login-splash__brand {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: baseline;
  gap: 0;
  margin: 0;
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei UI", "Microsoft YaHei",
    -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: clamp(1.85rem, 7.5vw, 3.15rem);
  font-weight: 700;
  letter-spacing: 0.12em;
  line-height: 1.2;
  overflow: hidden;
  animation: loginSplashBrandGlow 0.55s ease-in-out 0.92s 1 both;
}

.login-splash__brand::after {
  content: "";
  position: absolute;
  top: -30%;
  left: -45%;
  width: 38%;
  height: 160%;
  background: linear-gradient(
    105deg,
    transparent 0%,
    rgba(255, 255, 255, 0.05) 35%,
    rgba(255, 255, 255, 0.92) 50%,
    rgba(255, 255, 255, 0.05) 65%,
    transparent 100%
  );
  transform: skewX(-22deg);
  animation: loginSplashSweep 0.95s ease-in-out 0.72s forwards;
  pointer-events: none;
  mix-blend-mode: soft-light;
}

.login-splash__char {
  display: inline-block;
  opacity: 0;
  transform: translateY(0.35em);
  color: #2f4ad8;
  text-shadow: 0 0 0 rgba(66, 83, 255, 0.35);
  animation: loginSplashChar 0.42s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.login-splash__char--hyphen {
  margin: 0 0.02em;
  font-weight: 600;
  opacity: 0;
  animation: loginSplashChar 0.38s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

@keyframes loginSplashChar {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes loginSplashSweep {
  0% {
    left: -45%;
    opacity: 0;
  }
  12% {
    opacity: 1;
  }
  100% {
    left: 125%;
    opacity: 0;
  }
}

@keyframes loginSplashBrandGlow {
  0%,
  100% {
    filter: brightness(1) drop-shadow(0 0 0 rgba(66, 83, 255, 0));
  }
  50% {
    filter: brightness(1.14) drop-shadow(0 0 16px rgba(66, 83, 255, 0.38));
  }
}

.login-splash__tagline {
  margin: 14px 0 0;
  padding: 0;
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei UI", "Microsoft YaHei",
    -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: clamp(0.95rem, 3.8vw, 1.15rem);
  font-weight: 500;
  letter-spacing: 0.35em;
  color: #4253ff;
  line-height: 1.5;
  text-align: center;
  opacity: 0;
  transform: translateY(0.5rem);
  animation: loginSplashTagline 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.62s forwards;
}

@keyframes loginSplashTagline {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.splash-fade-leave-active {
  transition: opacity 0.32s ease;
}

.splash-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .login-splash__char,
  .login-splash__char--hyphen {
    animation: none;
    opacity: 1;
    transform: none;
  }

  .login-splash__brand::after {
    animation: none;
    display: none;
  }

  .login-splash__brand {
    animation: none;
  }

  .login-splash__tagline {
    animation: none;
    opacity: 1;
    transform: none;
  }

  .splash-fade-leave-active {
    transition-duration: 0.15s;
  }
}

.login-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f2f6ff;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  padding-left: max(32px, env(safe-area-inset-left, 0px));
  padding-right: max(32px, env(safe-area-inset-right, 0px));
  box-sizing: border-box;
}

.login-form-wrap {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.login-content {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding: 40px 36px 44px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid rgba(10, 28, 68, 0.06);
  box-shadow:
    0 4px 24px rgba(10, 28, 68, 0.06),
    0 1px 3px rgba(10, 28, 68, 0.04);
  animation: loginReveal 0.5s ease forwards;
}

@keyframes loginReveal {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-title {
  margin: 0 0 10px;
  font-size: 32px;
  font-weight: 600;
  color: #0a1c44;
  letter-spacing: 0.02em;
}

.login-subtitle {
  margin: 0 0 36px;
  font-size: 15px;
  color: #6b7a94;
  font-weight: 400;
  line-height: 1.5;
}

.login-form >>> .el-form-item {
  margin-bottom: 24px;
}

.login-form >>> .el-form-item:last-child {
  margin-bottom: 0;
  margin-top: 8px;
}

.login-remember-item {
  margin-bottom: 8px !important;
}

.login-remember-item >>> .el-checkbox__label {
  font-size: 14px;
  color: #6b7a94;
}

.register-to-login {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 12px;
  margin: 18px 0 0;
  font-size: 14px;
  color: #6b7a94;
  line-height: 1.5;
}

.register-to-login__left {
  text-align: left;
}

.register-to-login__right {
  margin-left: auto;
  flex-shrink: 0;
}

.login-agree-outer {
  margin-top: 20px;
  padding: 0 4px;
}

.login-agree-item {
  margin-bottom: 0 !important;
}

.login-agree-item >>> .el-form-item__content {
  line-height: 1.55;
}

.login-agree-item >>> .el-checkbox {
  align-items: flex-start;
  white-space: normal;
}

.login-agree-item >>> .el-checkbox__label {
  font-size: 13px;
  color: #6b7a94;
  line-height: 1.55;
  padding-left: 8px;
}

.login-agree-link {
  color: #4253ff;
  text-decoration: none;
  font-weight: 500;
}

.login-agree-link:hover {
  text-decoration: underline;
}

.login-agree-text {
  color: #6b7a94;
}

.login-input >>> .el-input__inner {
  height: 50px;
  line-height: 50px;
  padding-left: 42px;
  border-radius: 12px;
  background: #f4f6fa;
  border: 1px solid rgba(10, 28, 68, 0.08);
  font-size: 16px;
  color: #0a1c44;
  transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
}

.login-input >>> .el-input__inner:hover {
  background: #eef1f6;
  border-color: rgba(10, 28, 68, 0.12);
}

.login-input >>> .el-input__prefix {
  left: 12px;
  color: #8b95a8;
}

.login-input >>> .el-input__prefix .el-input__icon {
  font-size: 18px;
  line-height: 50px;
}

.login-input >>> .el-input__inner::placeholder {
  font-size: 16px;
  color: #9aa5b8;
}

.login-input >>> .el-input__inner:focus {
  background: #ffffff;
  border-color: #4253ff;
  box-shadow: 0 0 0 3px rgba(66, 83, 255, 0.12);
}

.login-btn {
  width: 100%;
  min-height: 50px;
  height: 50px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.12em;
  border-radius: 12px;
  border: none !important;
  background: linear-gradient(135deg, #4253ff 0%, #3546e8 100%) !important;
  color: #fff !important;
  transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;
  -webkit-tap-highlight-color: transparent;
}

.login-btn:hover,
.login-btn:focus {
  background: linear-gradient(135deg, #5364ff 0%, #4253ff 100%) !important;
  color: #fff !important;
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(66, 83, 255, 0.35);
}

.login-btn:active {
  transform: translateY(0);
}

.login-btn__icon {
  margin-right: 8px;
  font-size: 16px;
  vertical-align: middle;
}

@media (max-width: 768px) {
  .login-page {
    padding-top: max(20px, env(safe-area-inset-top, 0px));
    padding-bottom: max(20px, env(safe-area-inset-bottom, 0px));
    padding-left: max(28px, env(safe-area-inset-left, 0px));
    padding-right: max(28px, env(safe-area-inset-right, 0px));
    align-items: center;
    justify-content: center;
  }

  .login-content {
    max-width: 100%;
    padding: 28px 22px 32px;
    border-radius: 16px;
  }

  .login-title {
    font-size: 26px;
  }

  .login-subtitle {
    font-size: 14px;
    margin-bottom: 28px;
  }

  .login-form >>> .el-form-item {
    margin-bottom: 20px;
  }

  .login-input >>> .el-input__inner {
    height: 48px;
    line-height: 48px;
    padding-left: 40px;
    font-size: 16px;
  }

  .login-input >>> .el-input__prefix .el-input__icon {
    line-height: 48px;
    font-size: 17px;
  }

  .login-btn {
    min-height: 48px;
    height: 48px;
  }
}

@media (max-width: 375px) {
  .login-content {
    padding: 24px 18px 28px;
  }

  .login-title {
    font-size: 24px;
  }
}
</style>
